import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OrderReceived = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState(null);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const orderId = urlParams.get('order_id');
    const paymentMethod = urlParams.get('payment_method');
    const orderKey = urlParams.get('order_key');
    
    if (!orderId) {
      navigate('/');
      return;
    }
    
    setOrderDetails({
      orderId,
      paymentMethod,
      orderKey
    });
  }, [location, navigate]);

  const getPaymentInstructions = (paymentMethod) => {
    switch (paymentMethod) {
      case 'bacs':
        return {
          title: 'Direct Bank Transfer',
          instructions: [
            'Please make your payment directly into our bank account.',
            'Use your order number as the payment reference.',
            'Your order will be processed after we receive your payment.',
            '',
            'Bank Details:',
            'Account Name: Your Company Name',
            'Account Number: 123456789',
            'Bank: Your Bank Name',
            'IBAN: MK07 1234 5678 9012 3456'
          ]
        };
      case 'cheque':
        return {
          title: 'Check Payment',
          instructions: [
            'Please send a check to our store address.',
            'Make the check payable to: Your Company Name',
            'Include your order number on the check memo.',
            'Your order will be processed after we receive your payment.',
            '',
            'Mail to:',
            'Your Company Name',
            'Your Address',
            'City, Postal Code',
            'Macedonia'
          ]
        };
      default:
        return {
          title: 'Payment Instructions',
          instructions: ['Please follow the payment instructions sent to your email.']
        };
    }
  };

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
          <p className="mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  const paymentInfo = getPaymentInstructions(orderDetails.paymentMethod);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-light mb-2">Order Received</h1>
          <p className="text-black">Thank you for your order!</p>
        </div>

        <div className="rounded-lg p-6 mb-8">
          <h2 className="text-lg font-medium mb-4">Order Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-[#BDBDBD]">Order Number</p>
              <p className="font-medium">#{orderDetails.orderId}</p>
            </div>
            <div>
              <p className="text-sm text-[#BDBDBD]">Date</p>
              <p className="font-medium">{new Date().toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-[#BDBDBD]">Payment Method</p>
              <p className="font-medium">{paymentInfo.title}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-medium mb-4 text-blue-800">Payment Instructions</h3>
          <div className="space-y-2">
            {paymentInfo.instructions.map((instruction, index) => (
              <p key={index} className={`text-blue-700 ${instruction === '' ? 'mt-4' : ''}`}>
                {instruction}
              </p>
            ))}
          </div>
        </div>

        <div className="text-center space-y-4">
          <p className="text-[#BDBDBD]">
            We've sent an order confirmation email with payment details to your email address.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/')}
              className="bg-black text-white px-6 py-2 rounded hover:bg-[#BDBDBD] transition-colors"
            >
              Continue Shopping
            </button>
            <button
              onClick={() => navigate('/my-account/orders')}
              className="border border-black text-black px-6 py-2 rounded hover:bg-[#BDBDBD] transition-colors"
            >
              View Orders
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderReceived;